import { notification } from 'antd'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { UseMutateFunction, useMutation, useQueryClient, UseMutationOptions } from 'react-query'

import { QUERY_KEYS } from '@api'
import { AxiosErrorType, RailcarFilePayload } from '@types'

import RailcarApi from './api'

interface DeleteOptions {
  onSuccess?: () => void
  onError?: (error: unknown) => void
}

interface UseRailcarFilesReturn {
  uploadRailcarFile: UseMutateFunction<any, unknown, RailcarFilePayload, unknown>
  uploadRailcarFileIsFetching: boolean
  deleteRailcarFile: (fileId: number | string, options?: DeleteOptions) => void
  deleteRailcarFileIsFetching: boolean
}

const useRailcarFiles = (): UseRailcarFilesReturn => {
  const queryClient = useQueryClient()
  const { t } = useTranslation()
  const [uploadRailcarFileIsFetching, setUploadRailcarFileIsFetching] = useState(false)
  const [deleteRailcarFileIsFetching, setDeleteRailcarFileIsFetching] = useState(false)

  const uploadOnError = (error: unknown) => {
    setUploadRailcarFileIsFetching(false)
    const errorData = error as AxiosErrorType

    if (errorData.data.error.message.toLowerCase() === 'extension not allowed') {
      notification.error({
        message: t('format_error'),
      })
    } else if (errorData.data.error.type.toLowerCase() === 'invalid_file_size') {
      notification.error({
        message: t('file_size_error'),
      })
    } else {
      notification.error({
        message: t('generic_error'),
      })
    }
  }

  const uploadOnSuccess = () => {
    notification.success({
      message: t('successful_upload'),
    })
    queryClient.invalidateQueries([QUERY_KEYS.RAILCAR])
    setUploadRailcarFileIsFetching(false)
  }

  const { mutate: uploadRailcarFile } = useMutation(RailcarApi.uploadRailcarFile, {
    onError: uploadOnError,
    onSuccess: uploadOnSuccess,
    onMutate: () => {
      setUploadRailcarFileIsFetching(true)
    },
  })

  const deleteRailcarFileMutationOptions: UseMutationOptions<
    any,
    unknown,
    { fileId: number | string; options?: DeleteOptions },
    unknown
  > = {
    mutationFn: ({ fileId }) => RailcarApi.deleteRailcarFile(fileId),
    onError: (error, { options }) => {
      setDeleteRailcarFileIsFetching(false)
      notification.error({
        message: t('generic_error'),
      })
      options?.onError?.(error)
    },
    onSuccess: (data, { options }) => {
      notification.success({
        message: t('successful_delete'),
      })
      queryClient.invalidateQueries([QUERY_KEYS.RAILCAR])
      setDeleteRailcarFileIsFetching(false)
      options?.onSuccess?.()
    },
    onMutate: () => {
      setDeleteRailcarFileIsFetching(true)
    },
  }

  const { mutate: deleteRailcarFileMutation } = useMutation(deleteRailcarFileMutationOptions)

  const deleteRailcarFile = (fileId: number | string, options?: DeleteOptions) => {
    deleteRailcarFileMutation({ fileId, options })
  }

  return {
    uploadRailcarFile,
    uploadRailcarFileIsFetching,
    deleteRailcarFile,
    deleteRailcarFileIsFetching,
  }
}

export { useRailcarFiles }
